import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useUserData } from "hooks";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import type { Schema } from "schema/data/resource";
import outputs from "amplify_outputs.json";

import {
  ChatSession,
  CurrentChatSession,
  ChatMessage,
  ChatInput,
  ChatAgentInput,
  ChatContextType,
} from "types";
import { useChatOperations } from "hooks";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

// ChatProvider component
export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { userData } = useUserData();
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [nextTokenMsg, setNextTokenMsg] = useState<string | null>(null);
  const [loadingMsg, setLoadingMsg] = useState<boolean>(false);
  const [currentChatSession, setCurrentChatSession] =
    useState<CurrentChatSession | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [userId, setUserId] = useState<string>("Unknown");
  const [tokenid, setTokenid] = useState<string | undefined>(undefined);
  const [isDrawerVisible, setIsDrawerVisible] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Get operations from custom hook
  const {
    updateChatSessionsWithUnreadCount: updateUnreadCountOp,
    fetchChatSessions: fetchSessionsOp,
    fetchMessagesBySessionId: fetchMessagesOp,
    createChatSession: createSessionOp,
    updateUserLastSeen: updateLastSeenOp,
    deleteSessionAndMessages: deleteSessionOp,
    sendAgent: sendAgentOp,
    sendMessage: sendMessageOp,
    calculateTotalUnreadCount: calculateUnreadOp,
  } = useChatOperations(tokenid, userId);

  // References for subscription use
  const chatMessagesRef = useRef(chatMessages);
  const currentChatSessionRef = useRef(currentChatSession);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleDrawer = useCallback(() => {
    if (windowWidth < 768) {
      setIsDrawerVisible(!isDrawerVisible);
    }
  }, [isDrawerVisible, windowWidth]);

  // Update chat sessions with unread count
  const updateChatSessionsWithUnreadCount = useCallback(
    async (chatSessions: ChatSession[]) => {
      await updateUnreadCountOp(
        chatSessions,
        currentChatSession?.id,
        setChatSessions,
      );
    },
    [currentChatSession?.id, updateUnreadCountOp],
  );

  // Calculate total unread count
  const calculateTotalUnreadCount = useCallback(
    (chatSessions: ChatSession[]): number => {
      return calculateUnreadOp(chatSessions, currentChatSession?.id);
    },
    [calculateUnreadOp, currentChatSession?.id],
  );

  // Fetch chat sessions
  const fetchChatSessions = useCallback(async () => {
    await fetchSessionsOp(setChatSessions, updateChatSessionsWithUnreadCount);
  }, [fetchSessionsOp, updateChatSessionsWithUnreadCount]);

  // Fetch messages by session ID
  const fetchMessagesBySessionId = useCallback(
    async (sessionId: string, nextToken: string | null) => {
      if (loadingMsg) return;
      await fetchMessagesOp(
        sessionId,
        nextToken,
        setLoadingMsg,
        setChatMessages,
        setNextTokenMsg,
      );
    },
    [fetchMessagesOp, loadingMsg],
  );

  // Create chat session
  const createChatSession = useCallback(
    async (users: string[], id?: string): Promise<ChatSession> => {
      const newSession = await createSessionOp(users, id);
      setChatSessions((prevSessions) => [...prevSessions, newSession]);
      return newSession;
    },
    [createSessionOp],
  );

  // Update user last seen
  const updateUserLastSeen = useCallback(async () => {
    if (currentChatSession) {
      const currentTimestamp = await updateLastSeenOp(
        currentChatSession.id,
        currentChatSession.users,
      );
      if (currentTimestamp) {
        setChatSessions((prevChatSessions) =>
          prevChatSessions.map((session) =>
            session.id === currentChatSession.id
              ? { ...session, unreadCount: 0, userLastSeen: currentTimestamp }
              : session,
          ),
        );
      }
    }
  }, [currentChatSession, updateLastSeenOp]);

  // Delete session and messages
  const deleteSessionAndMessages = useCallback(
    async (sessionId: string, users: string[]) => {
      await deleteSessionOp(
        sessionId,
        users,
        setChatMessages,
        setChatSessions,
        setCurrentChatSession,
      );
    },
    [deleteSessionOp],
  );

  // Send agent message
  const sendAgent = useCallback(
    async (input: ChatAgentInput) => {
      try {
        console.log("sendAgent called with input:", input);
        // For Cold Start and lambda
        const pseudoMsg = {
          ...input,
          id: "PsudoMsg",
          createdAt: null,
        };
        setChatMessages((prevMessages) => [pseudoMsg, ...prevMessages]);

        const result = await sendAgentOp(input);
        console.log("sendAgent result:", result);
        return result;
      } catch (error) {
        console.error("Error sending agent message:", error);
      }
    },
    [sendAgentOp],
  );

  // Send normal message
  const sendMessage = useCallback(
    async (input: ChatInput) => {
      return await sendMessageOp(input);
    },
    [sendMessageOp],
  );

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTo(0, 0);
    }
  };

  // Fetch user ID and token
  useEffect(() => {
    async function fetchUserId() {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        const emailFromToken =
          session.tokens?.idToken?.payload.email?.toString();

        if (emailFromToken) {
          setUserId(emailFromToken);

          setTokenid(token);
        } else {
          setUserId("Unknown");
        }
      } catch (error) {
        console.error("Error getting current user");
        setUserId("Unknown");
      }
    }
    fetchUserId();
  }, [userData]);

  // Fetch chat sessions when user ID and token are available
  useEffect(() => {
    if (userId && tokenid) {
      fetchChatSessions();
    }
  }, [userId, tokenid, fetchChatSessions]);

  // Update refs when state changes
  useEffect(() => {
    chatMessagesRef.current = chatMessages;
    currentChatSessionRef.current = currentChatSession;
    console.log("Updated currentChatSessionRef to:", currentChatSession?.id);
  }, [chatMessages, currentChatSession]);

  // Subscription for new messages
  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    let retryTimeout: NodeJS.Timeout | null = null;

    const setupSubscription = async () => {
      try {
        if (!tokenid) {
          console.warn("Token not available for subscription");
          return;
        }

        subscription = await client.models.Message.onCreate({
          authMode: "userPool",
          authToken: tokenid,
        }).subscribe({
          next: (data: any) => {
            const newMessage = data;
            console.log(
              "Received message for sessionId:",
              newMessage.sessionId,
            );
            console.log(
              "Current session ID:",
              currentChatSessionRef.current?.id,
            );

            if (
              currentChatSessionRef.current &&
              newMessage.sessionId === currentChatSessionRef.current.id
            ) {
              console.log("Message belongs to current session, adding to chat");
              if (chatMessagesRef.current[0]?.id === "PsudoMsg") {
                setChatMessages((prevMessages) => [
                  newMessage,
                  ...prevMessages.slice(1),
                ]);
              } else {
                setChatMessages((prevMessages) => [
                  newMessage,
                  ...prevMessages,
                ]);
              }

              setChatSessions((prevChatSessions) =>
                prevChatSessions.map((session) =>
                  session.id === currentChatSessionRef.current?.id
                    ? {
                        ...session,
                        unreadCount: 0,
                        lastMessage: JSON.parse(newMessage.content).message,
                      }
                    : session,
                ),
              );
              scrollToBottom();
            } else {
              console.log(
                "Message belongs to different session, updating session list only",
              );
              setChatSessions((prevChatSessions) => {
                const sessionExists = prevChatSessions.some(
                  (session) => session.id === newMessage.sessionId,
                );

                if (sessionExists) {
                  return prevChatSessions.map((session) =>
                    session.id === newMessage.sessionId
                      ? {
                          ...session,
                          unreadCount: (session.unreadCount || 0) + 1,
                          lastMessage: JSON.parse(newMessage.content).message,
                        }
                      : session,
                  );
                } else {
                  return [
                    ...prevChatSessions,
                    {
                      id: newMessage.sessionId,
                      unreadCount: 1,
                      lastMessage: JSON.parse(newMessage.content).message,
                      users: newMessage.users,
                      createdAt: newMessage.createdAt,
                      updatedAt: newMessage.updatedAt,
                      userLastSeen: new Date(0),
                    },
                  ];
                }
              });
            }
          },
          error: (error: any) => {
            console.error("Subscription error:", error);
            // Only retry if we have valid email and token
            if (userId !== "Unknown" && tokenid) {
              retryTimeout = setTimeout(() => {
                console.log("Retrying subscription setup...");
                setupSubscription();
              }, 2000);
            }
          },
        });
        console.log("Subscription setup successful");
      } catch (error) {
        console.error("Error setting up subscription:", error);
        // Only retry if we have valid email and token
        if (userId !== "Unknown" && tokenid) {
          retryTimeout = setTimeout(() => {
            console.log("Retrying subscription setup...");
            setupSubscription();
          }, 2000);
        }
      }
    };

    if (userId !== "Unknown" && tokenid) {
      console.log("Setup Subscription");
      setupSubscription();
    }

    return () => {
      if (subscription) {
        console.log("Unsubscribe");
        subscription.unsubscribe();
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [tokenid, userId]);

  return (
    <ChatContext.Provider
      value={{
        currentChatSession,
        setCurrentChatSession,
        chatSessions,
        fetchChatSessions,
        chatMessages,
        setChatMessages,
        fetchMessagesBySessionId,
        createChatSession,
        updateUserLastSeen,
        updateChatSessionsWithUnreadCount,
        sendAgent,
        messagesEndRef,
        scrollToBottom,
        sendMessage,
        deleteSessionAndMessages,
        loadingMsg,
        setLoadingMsg,
        nextTokenMsg,
        setNextTokenMsg,
        calculateTotalUnreadCount,
        toggleDrawer,
        isDrawerVisible,
        setIsDrawerVisible,
        userId,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
export default ChatProvider;
